import { useDepot } from '#~/depot/hooks.js';
import { useEffect, useRef, type ReactElement } from 'react';
import { shallow } from 'zustand/shallow';
import { Paths } from '../constants.js';
import { TimeoutWatchdog } from '../room/timeout-watchdog.js';
import { toRootBootError } from './cross-account-boot-error.js';
import { Placeholder } from './placeholder.jsx';
import type { Props } from './types.js';
import { useRootBootstrap } from './use-root-bootstrap.js';

const TIMEOUT_WATCHDOG_TIMEOUT = 17_000;

/**
 * `<Root>` component.
 *
 * Per the gyfky-W1 spec: Root renders `<Placeholder>` UNCONDITIONALLY
 * so the studio DOM mounts the moment routing arrives here. The boot
 * UX (spinner + "Loading your studio…") is now owned by the single
 * `<BootOverlay>` mounted once in `router.tsx`; this component just
 * keeps readiness pending until the destination route publishes its
 * actionable state.
 *
 * Per `stay-in-scope` (ai.knowledge/global): the prior `!isReady`
 * ternary that swapped `<AutoLinkLoadingScreen>` for `<Placeholder>`
 * was the source of the `Guard → Root` remount flash. Removing the
 * ternary is THE structural fix.
 *
 * Per `acknowledge-rules-publicly` (ai.knowledge/global) on
 * `verify-design-intent-before-removal`: `<TimeoutWatchdog>` is NOT
 * removed — it sits as a sibling of `<Placeholder>` so the 17s
 * safety net still arms when Root mounts. Its presence was load-bearing
 * (the "if iris truly never boots, restart" recovery) and is preserved.
 */
export const Root = (props: Props): ReactElement => {
  const { accountId, error, linkedAgentID, watchdogEnabled } = useRootBootstrap();
  const produce = useDepot((µ) => µ.produce, shallow);
  const ownedRetryRef = useRef<(() => void) | undefined>(undefined);

  // Root chooses a route; the mounted destination owns the actionable-ready publish.
  useEffect(() => {
    produce((draft) => {
      draft.boot.isRootReady = false;

      if (error) {
        // Show recovery above the blocked subtree; an error below the overlay is unreachable.
        const retryFn = (): void => window.location.reload();
        // ENG-4685: a cross-account Bridge link cannot be retried into success by
        // the same account; the mapped error carries this Sign-out escape. Same
        // full navigation the auth synchronizer uses for its forced logout.
        const signOutFn = (): void => {
          window.location.href = `/${Paths.logout}`;
        };
        ownedRetryRef.current = retryFn;
        draft.boot.error = toRootBootError(error, retryFn, signOutFn);
      } else if (draft.boot.error?.retryFn === ownedRetryRef.current) {
        // Root retracts only the error object it published. Other bootstrap
        // producers retain ownership of their own recovery surfaces.
        draft.boot.error = null;
        ownedRetryRef.current = undefined;
      }
    });
  }, [accountId, error, produce]);

  return (
    <>
      <Placeholder {...props} />
      <TimeoutWatchdog
        enabled={watchdogEnabled}
        timeout={TIMEOUT_WATCHDOG_TIMEOUT}
        agentIDs={linkedAgentID ? [linkedAgentID] : []}
      />
    </>
  );
};
